import { formatAttorneyFee } from "@/lib/utils/attorneyFeeUtils";
import { AddOrEditMatterStatusModal } from "@/components/features/settings/defaults/matter-statuses/AddOrEditMatterStatusModal";
import { MatterStatusContextMenu } from "@/components/features/settings/defaults/matter-statuses/MatterStatusContextMenu";
import { ReorderMatterStatusesModal } from "@/components/features/settings/defaults/matter-statuses/ReorderMatterStatusesModal";
import {
  ActionEmptyState,
  ActionConfirmationModal,
  Button,
  Card,
  CardContent,
  Text,
} from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterStatusCategoriesQueryOptions } from "@/lib/data/react-query";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { useDeleteMatterStatus } from "@/lib/data/react-query/matters/matter-status";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  getMatterStatusCategoryLabel,
  type MatterStatusCategoryOption,
} from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterStatus, type MatterStatusCategory } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { orderBy } from "lodash";
import { ChevronRight, CircleDot, Pencil, Plus, Trash2 } from "lucide-react";
import pluralize from "pluralize";
import { useMemo, useRef, useState } from "react";

const CATEGORY_ICON_COLORS: Record<
  MatterStatusCategoryOption,
  { surface: string; text: string }
> = {
  opportunity: {
    surface: "border-sky-100 bg-sky-50",
    text: "text-sky-600",
  },
  lead: {
    surface: "border-blue-100 bg-blue-50",
    text: "text-blue-600",
  },
  dismissed: {
    surface: "border-red-100 bg-red-50",
    text: "text-red-600",
  },
  "pre-litigation": {
    surface: "border-orange-100 bg-orange-50",
    text: "text-orange-500",
  },
  litigation: {
    surface: "border-purple-100 bg-purple-50",
    text: "text-purple-600",
  },
  closed: {
    surface: "border-emerald-100 bg-emerald-50",
    text: "text-emerald-600",
  },
  referred: {
    surface: "border-neutral-200 bg-neutral-100",
    text: "text-neutral-500",
  },
  declined: {
    surface: "border-red-100 bg-red-50",
    text: "text-red-600",
  },
};

function formatMatterStatusDefaultAttorneyFee(
  value: MatterStatus["defaultAttorneyFee"],
): string | null {
  if (value == null || value === 0) {
    return null;
  }
  return `${formatAttorneyFee(value)}%`;
}

type MatterStatusesSettingsViewProps = {
  title?: string;
  addButtonLabel?: string;
  categoryOptions?: readonly string[];
  categoryFilter?: readonly string[];
};

function MatterStatusesSettingsViewInner({
  title = "Matter statuses",
  addButtonLabel = "Add status",
  categoryOptions,
  categoryFilter,
}: MatterStatusesSettingsViewProps) {
  const [isAddingStatus, setIsAddingStatus] = useState(false);
  const [addingStatusCategory, setAddingStatusCategory] = useState<
    string | null
  >(null);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [activeStatusId, setActiveStatusId] = useState<string | null>(null);
  const [statusPendingDelete, setStatusPendingDelete] =
    useState<MatterStatus | null>(null);
  const reorderTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [expandedCategory, setExpandedCategory] = useState("");
  const [reorderCategory, setReorderCategory] = useState<string | null>(null);

  const { hasActionPermission } = useUser();

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());
  const {
    data: matterStatusCategoriesData,
    isLoading: isMatterStatusCategoriesLoading,
    isError: isMatterStatusCategoriesError,
  } = useQuery(retrieveMatterStatusCategoriesQueryOptions());

  const deleteMatterStatus = useDeleteMatterStatus();
  const resolvedCategories = useMemo(() => {
    const categories = (matterStatusCategoriesData?.data ?? []).map(
      (category) => ({
        ...category,
        label: getMatterStatusCategoryLabel(category.id),
      }),
    );
    const filter = categoryOptions ?? categoryFilter;
    if (!filter?.length) {
      return categories;
    }
    const allowedCategoryIds = new Set(filter);
    return categories.filter((category) => allowedCategoryIds.has(category.id));
  }, [categoryFilter, categoryOptions, matterStatusCategoriesData?.data]);
  const resolvedCategoryIds = useMemo(
    () => resolvedCategories.map((category) => category.id),
    [resolvedCategories],
  );
  const categoriesById = useMemo(
    () =>
      new Map<string, MatterStatusCategory>(
        resolvedCategories.map((category) => [category.id, category]),
      ),
    [resolvedCategories],
  );

  const matterStatuses = useMemo(
    () =>
      (matterSettingsData?.data.statuses ?? []).filter((status) =>
        resolvedCategoryIds.includes(status.categoryId),
      ),
    [matterSettingsData?.data.statuses, resolvedCategoryIds],
  );

  const statusesByCategory = useMemo(() => {
    const map = new Map<string, MatterStatus[]>();
    for (const cat of resolvedCategoryIds) {
      map.set(cat, []);
    }
    for (const status of matterStatuses) {
      const list = map.get(status.categoryId);
      if (list) {
        list.push(status);
      }
    }
    for (const cat of resolvedCategoryIds) {
      const sorted = orderBy(
        map.get(cat) ?? [],
        [(s) => s.order ?? Number.POSITIVE_INFINITY, "label", "id"],
        ["asc", "asc", "asc"],
      );
      map.set(cat, sorted);
    }
    return map;
  }, [matterStatuses, resolvedCategoryIds]);

  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatus,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatus,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatus,
    ACTION_PERMISSION_VALUES.delete,
  );

  if (isMatterSettingsError || isMatterStatusCategoriesError) {
    return <Text variant="subHeading">{serverErrorFallbackText}</Text>;
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="gap-y-1">
          <Text variant="title">{title}</Text>
        </div>
        {canCreate ? (
          <Button
            variant="foreground"
            size="md"
            className="shrink-0"
            onClick={() => {
              setAddingStatusCategory(null);
              setIsAddingStatus(true);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {addButtonLabel}
          </Button>
        ) : null}
      </div>

      {isMatterSettingsLoading || isMatterStatusCategoriesLoading ? (
        <div className="flex flex-col gap-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <Card key={index} className="border-border/60 shadow-sm">
              <CardContent className="p-4 sm:p-6">
                <div className="h-12 w-full animate-pulse rounded-md bg-neutral-100" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : matterStatuses.length === 0 ? (
        <ActionEmptyState
          title="No statuses yet"
          description="Add a status to start tracking matter progress."
          actions={
            canCreate ? (
              <Button
                onClick={() => {
                  setAddingStatusCategory(null);
                  setIsAddingStatus(true);
                }}
              >
                Add status
              </Button>
            ) : null
          }
        />
      ) : (
        <Accordion
          value={expandedCategory}
          onValueChange={setExpandedCategory}
          type="single"
          collapsible
          className="flex w-full flex-col gap-4"
        >
          {resolvedCategoryIds.map((category) => {
            const list = statusesByCategory.get(category) ?? [];
            const count = list.length;
            const colors =
              CATEGORY_ICON_COLORS[category as MatterStatusCategoryOption];
            const categoryTitle =
              categoriesById.get(category)?.label ??
              getMatterStatusCategoryLabel(category);

            return (
              <AccordionItem
                key={category}
                value={category}
                className="border-0"
              >
                <Card className="border-border/60 overflow-hidden shadow-sm">
                  <CardContent className="p-0">
                    <div className="group hover:bg-light-primary/50 flex w-full cursor-pointer items-center px-4 py-5 transition-colors sm:px-6">
                      <AccordionTrigger
                        className="min-w-0 flex-1 cursor-pointer items-center py-0 hover:bg-transparent hover:no-underline [&[data-state=open]_.status-chevron]:rotate-90"
                        icon={<></>}
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-3 pr-2 text-left">
                          <ChevronRight
                            className="status-chevron text-muted-foreground h-4 w-4 shrink-0 self-center transition-transform duration-200"
                            aria-hidden
                          />
                          <div
                            className={cn(
                              "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border",
                              colors.surface,
                            )}
                            aria-hidden
                          >
                            <CircleDot className={cn("h-4 w-4", colors.text)} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <Text variant="body">{categoryTitle}</Text>
                            <Text variant="subHeading" className="mt-0.5">
                              {count} {pluralize("status", count)}
                            </Text>
                          </div>
                        </div>
                      </AccordionTrigger>
                      {canUpdate &&
                      count > 1 &&
                      expandedCategory === category ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={(event) => {
                            reorderTriggerRef.current = event.currentTarget;
                            setReorderCategory(category);
                          }}
                        >
                          Reorder
                        </Button>
                      ) : null}
                    </div>
                    <AccordionContent className="px-4 pb-4 sm:px-6">
                      <div className="divide-border/60 border-border/60 divide-y border-t">
                        {count === 0 ? (
                          <div className="flex flex-col items-center gap-4 py-8 text-center">
                            <Text variant="subHeading">
                              No statuses in this category yet.
                            </Text>
                            {canCreate ? (
                              <Button
                                onClick={() => {
                                  setAddingStatusCategory(category);
                                  setIsAddingStatus(true);
                                }}
                              >
                                <Plus className="h-4 w-4" />
                                Add status
                              </Button>
                            ) : null}
                          </div>
                        ) : (
                          <>
                            {list.map((status) => {
                              const attorneyFeeDisplay =
                                formatMatterStatusDefaultAttorneyFee(
                                  status.defaultAttorneyFee,
                                );
                              return (
                                <MatterStatusContextMenu
                                  key={status.id}
                                  status={status}
                                  canEdit={canUpdate}
                                  canDelete={canDelete}
                                  onEdit={() => {
                                    setActiveStatusId(status.id);
                                    setIsEditingStatus(true);
                                  }}
                                  onDelete={() => {
                                    setStatusPendingDelete(status);
                                  }}
                                  trigger={
                                    <div className="group hover:bg-light-primary/50 flex items-start gap-2 py-5 transition-colors sm:gap-3">
                                      <div className="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
                                        <div className="min-w-0 flex-1">
                                          <Text variant="body">
                                            {status.label}
                                          </Text>
                                          {status.description ? (
                                            <Text
                                              variant="caption"
                                              className="mt-0.5"
                                            >
                                              {status.description}
                                            </Text>
                                          ) : null}
                                        </div>
                                        <div className="flex shrink-0 flex-row flex-wrap items-start justify-between gap-6 sm:justify-end">
                                          {status.goal != null ? (
                                            <div className="min-w-[4.5rem]">
                                              <Text
                                                variant="caption"
                                                className="font-medium tracking-wide uppercase"
                                              >
                                                Goal
                                              </Text>
                                              <Text
                                                variant="subHeading"
                                                className="mt-0.5"
                                              >
                                                {pluralize(
                                                  "day",
                                                  status.goal,
                                                  true,
                                                )}
                                              </Text>
                                            </div>
                                          ) : null}
                                          {attorneyFeeDisplay ? (
                                            <div className="min-w-[4.5rem]">
                                              <Text
                                                variant="caption"
                                                className="font-medium tracking-wide uppercase"
                                              >
                                                Attorney fee
                                              </Text>
                                              <Text
                                                variant="subHeading"
                                                className="mt-0.5"
                                              >
                                                {attorneyFeeDisplay}
                                              </Text>
                                            </div>
                                          ) : null}
                                          {canUpdate || canDelete ? (
                                            <div
                                              className={cn(
                                                "flex items-center gap-0.5 transition-opacity duration-150",
                                                "opacity-100 sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100",
                                              )}
                                            >
                                              {canUpdate ? (
                                                <Button
                                                  type="button"
                                                  variant="ghost"
                                                  size="sm"
                                                  className="h-9 w-9 p-0"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveStatusId(
                                                      status.id,
                                                    );
                                                    setIsEditingStatus(true);
                                                  }}
                                                >
                                                  <span className="sr-only">
                                                    Edit {status.label}
                                                  </span>
                                                  <Pencil
                                                    className="text-muted-foreground h-4 w-4"
                                                    aria-hidden
                                                  />
                                                </Button>
                                              ) : null}
                                              {canDelete ? (
                                                <Button
                                                  type="button"
                                                  variant="ghost"
                                                  size="sm"
                                                  className="text-destructive hover:text-destructive h-9 w-9 p-0"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setStatusPendingDelete(
                                                      status,
                                                    );
                                                  }}
                                                >
                                                  <span className="sr-only">
                                                    Delete {status.label}
                                                  </span>
                                                  <Trash2
                                                    className="h-4 w-4"
                                                    aria-hidden
                                                  />
                                                </Button>
                                              ) : null}
                                            </div>
                                          ) : null}
                                        </div>
                                      </div>
                                    </div>
                                  }
                                />
                              );
                            })}
                          </>
                        )}
                      </div>
                    </AccordionContent>
                  </CardContent>
                </Card>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {reorderCategory && canUpdate ? (
        <ReorderMatterStatusesModal
          categoryId={reorderCategory}
          categoryLabel={
            categoriesById.get(reorderCategory)?.label ??
            getMatterStatusCategoryLabel(reorderCategory)
          }
          statuses={statusesByCategory.get(reorderCategory) ?? []}
          onClose={() => {
            setReorderCategory(null);
          }}
          onRestoreFocus={() => {
            reorderTriggerRef.current?.focus();
          }}
        />
      ) : null}

      <AddOrEditMatterStatusModal
        editing={false}
        open={isAddingStatus}
        setOpen={(open) => {
          setIsAddingStatus(open);
          if (!open) {
            setAddingStatusCategory(null);
          }
        }}
        categoryOptions={resolvedCategories}
        defaultCategoryId={addingStatusCategory ?? undefined}
      />
      {Boolean(
        activeStatusId && matterStatuses.find((s) => s.id === activeStatusId),
      ) && (
        <AddOrEditMatterStatusModal
          activeMatterStatus={
            matterStatuses.find((s) => s.id === activeStatusId) as MatterStatus
          }
          isReadOnly={!canUpdate}
          editing
          open={isEditingStatus}
          setOpen={setIsEditingStatus}
          setActiveMatterStatusId={setActiveStatusId}
          categoryOptions={resolvedCategories}
        />
      )}
      <ActionConfirmationModal
        title="Are you sure you want to delete this matter status?"
        open={Boolean(statusPendingDelete)}
        setOpen={(open) => {
          if (!open) {
            setStatusPendingDelete(null);
          }
        }}
        isActionPending={deleteMatterStatus.isPending}
        afterActionFn={async () => {
          if (!statusPendingDelete) {
            return;
          }
          await deleteMatterStatus.mutateAsync({
            id: statusPendingDelete.id,
          });
          setStatusPendingDelete(null);
        }}
      />
    </div>
  );
}

export default function MatterStatusesSettingsView(
  props: MatterStatusesSettingsViewProps = {},
) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <MatterStatusesSettingsViewInner {...props} />
    </ErrorBoundary>
  );
}
